"use client";

import { AskTwinMark } from "@/components/app/sessions/ask-twin";
import Link from "next/link";
import {
  BrainIcon,
  BookOpenIcon,
  BookTextIcon,
  ChartColumnIcon,
  DatabaseIcon,
  NetworkIcon,
  ClockIcon,
  ShapesIcon,
  HelpCircleIcon,
  KeyboardIcon,
  ListChecksIcon,
  ListTodoIcon,
  MapIcon,
  MessageSquareWarningIcon,
  UserCheckIcon,
  ZapIcon,
} from "lucide-react";
import type { ReactNode, Ref } from "react";
import { Badge } from "@/components/ui/badge";

import {
  Menubar,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { compactInboxTime } from "@/lib/format";
import type { WorkspaceSession } from "@/lib/mlx-workspace-client";
import { cn } from "@/lib/utils";

export const menubarLinkClass =
  "flex items-center rounded-sm px-1.5 py-[2px] text-sm font-semibold outline-none select-none hover:bg-muted focus-visible:bg-muted";

function sessionTitle(session: Pick<WorkspaceSession, "id" | "title">): string {
  return session.title?.trim() || session.id;
}

export type AppMenubarViewProps = {
  barRef?: Ref<HTMLElement>;
  organizationControl: ReactNode;
  userControl: ReactNode;
  twinTriggerRef?: Ref<HTMLButtonElement>;
  twinOpen: boolean;
  syncDot: { label: string; dotClassName: string };
  onOpenTwin: () => void;
  /** On a phone the menus collapse into this one Menu button (AppPhoneMenu). */
  phoneMenu?: ReactNode;
  newSessionHref: string;
  onOpenNewSession: () => void;
  recentSessions: Pick<WorkspaceSession, "id" | "title" | "updatedAt">[];
  showFeed: boolean;
  /** The Twin entities surface, when the deployment enables it. */
  showEntities?: boolean;
  showInbox: boolean;
  inboxBadge?: ReactNode;
  showWorkflows: boolean;
  canManageOrganization: boolean;
  onStartTour: () => void;
  onOpenShortcuts: () => void;
  onSendFeedback: () => void;
  /** A guest uses only their channels: the bar shows the organisation and their account. */
  guest?: boolean;
};

/**
 * The application menu bar as markup: every value it shows arrives as a prop,
 * so it renders the same with live data (AppMenubar) or fixtures (the
 * marketing product bridge). Organisation, user and phone-menu controls are
 * slots because each auth mode and screen size brings its own.
 */
export function AppMenubarView({
  barRef,
  organizationControl,
  userControl,
  twinTriggerRef,
  twinOpen,
  syncDot,
  onOpenTwin,
  phoneMenu,
  newSessionHref,
  onOpenNewSession,
  recentSessions,
  showFeed,
  showEntities = false,
  showInbox,
  inboxBadge,
  showWorkflows,
  canManageOrganization,
  onStartTour,
  onOpenShortcuts,
  onSendFeedback,
  guest = false,
}: AppMenubarViewProps) {
  if (guest) {
    return (
      <header ref={barRef} className="menubar-surface sticky top-0 z-40 block">
        <div className="flex h-10 w-full items-center gap-2.5 pl-3 pr-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {organizationControl}
            <Badge variant="outline">Guest</Badge>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {userControl}
          </div>
        </div>
      </header>
    );
  }
  return (
    <header
      ref={barRef}
      data-tour="app-menubar"
      className="menubar-surface sticky top-0 z-40 block"
    >
      <div className="flex h-10 w-full items-center gap-2.5 pl-3 pr-4">
        {/* On a phone the menus collapse into one Menu button; the account
            menu stays pinned beside them. */}
        <div className="flex min-w-0 flex-1 items-center overflow-x-auto scrollbar-thin">
          <div className="flex min-w-max flex-1 items-center gap-2.5">
            {organizationControl}

            <div className="flex items-center gap-0.5">
              <button
                ref={twinTriggerRef}
                className={cn(menubarLinkClass, "gap-2")}
                data-tour="menu-sync"
                title={syncDot.label}
                aria-haspopup="dialog"
                aria-expanded={twinOpen}
                onClick={onOpenTwin}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-2 shrink-0 rounded-full ring-2",
                    syncDot.dotClassName,
                  )}
                />
                Twin
              </button>
              {phoneMenu}
              <Menubar className="hidden bg-transparent px-0 sm:flex">
                <MenubarMenu>
                  <MenubarTrigger data-tour="menu-agent">
                    <span>Agent</span>
                  </MenubarTrigger>
                  <MenubarContent align="start" className="w-72">
                    <MenubarGroup>
                      <MenubarItem
                        render={
                          <Link
                            href={newSessionHref}
                            onClick={(event) => {
                              if (
                                event.metaKey ||
                                event.ctrlKey ||
                                event.altKey ||
                                event.shiftKey
                              ) {
                                return;
                              }
                              event.preventDefault();
                              onOpenNewSession();
                            }}
                          />
                        }
                      >
                        <AskTwinMark />
                        New session
                        <MenubarShortcut><ShortcutKeys shortcuts={["n t", "space"]} /></MenubarShortcut>
                      </MenubarItem>
                      <MenubarItem
                        render={<Link href="/workspace/sessions?assignee=me" />}
                      >
                        <UserCheckIcon data-icon="inline-start" />
                        Assigned to me
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/workspace/sessions" />}>
                        <ListTodoIcon data-icon="inline-start" />
                        All sessions
                        <MenubarShortcut><ShortcutKeys shortcuts={["g t"]} /></MenubarShortcut>
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/memories" />}>
                        <BrainIcon data-icon="inline-start" />
                        Memories
                      </MenubarItem>
                      <MenubarItem
                        render={<Link href="/workspace/automations" />}
                      >
                        <ZapIcon data-icon="inline-start" />
                        Automations
                      </MenubarItem>
                    </MenubarGroup>
                    {recentSessions.length ? (
                      <>
                        <MenubarSeparator />
                        <MenubarGroup>
                          {recentSessions.map((session) => {
                            const title = sessionTitle(session);
                            return (
                              <MenubarItem
                                key={`recent-session-${session.id}`}
                                render={
                                  <Link
                                    href={`/workspace/sessions/${encodeURIComponent(session.id)}`}
                                  />
                                }
                              >
                                <ClockIcon data-icon="inline-start" />
                                <span
                                  className="max-w-[220px] truncate"
                                  title={title}
                                >
                                  {title}
                                </span>
                                {/* Two sessions can share a title; when each was last active tells them apart. */}
                                <MenubarShortcut className="pl-3 tabular-nums">
                                  {compactInboxTime(new Date(session.updatedAt).toISOString())}
                                </MenubarShortcut>
                              </MenubarItem>
                            );
                          })}
                        </MenubarGroup>
                      </>
                    ) : null}
                  </MenubarContent>
                </MenubarMenu>

                {showFeed ? (
                  <Link
                    href="/workspace/feed"
                    className={cn(menubarLinkClass, "gap-1")}
                    data-tour="menu-feed"
                  >
                    Feed
                  </Link>
                ) : null}
                {showInbox ? (
                  <Link
                    href="/workspace/inbox"
                    className={cn(menubarLinkClass, "gap-1")}
                    data-tour="menu-inbox"
                  >
                    Inbox
                    {inboxBadge}
                  </Link>
                ) : null}
                <Link
                  href="/workspace/channels"
                  className={menubarLinkClass}
                  data-tour="menu-channels"
                >
                  Channels
                </Link>
                <MenubarMenu>
                  <MenubarTrigger data-tour="menu-knowledge">
                    Knowledge
                  </MenubarTrigger>
                  <MenubarContent align="start" className="w-64">
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/knowledge" />}>
                        <BookOpenIcon data-icon="inline-start" />
                        Knowledge
                        <MenubarShortcut><ShortcutKeys shortcuts={["g d"]} /></MenubarShortcut>
                      </MenubarItem>
                      {showEntities ? (
                        <MenubarItem render={<Link href="/workspace/entities" />}>
                          <ShapesIcon data-icon="inline-start" />
                          Entities
                        </MenubarItem>
                      ) : null}
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/authoring/skills" />}>
                        <BookTextIcon data-icon="inline-start" />
                        Skills
                        <MenubarShortcut><ShortcutKeys shortcuts={["g k"]} /></MenubarShortcut>
                      </MenubarItem>
                    </MenubarGroup>
                    {canManageOrganization && (
                      <>
                        <MenubarSeparator />
                        {/* Tools only organisation admins see, named as such. */}
                        <MenubarGroup>
                          <MenubarLabel>Admin</MenubarLabel>
                          <MenubarItem render={<Link href="/admin/twin" />}>
                            <NetworkIcon data-icon="inline-start" />
                            Twin configuration
                          </MenubarItem>
                          <MenubarItem render={<Link href="/admin/data" />}>
                            <DatabaseIcon data-icon="inline-start" />
                            Data inspection
                          </MenubarItem>
                          {/* Runtime throughput and latency: an operator view. */}
                          <MenubarItem render={<Link href="/metrics" />}>
                            <ChartColumnIcon data-icon="inline-start" />
                            Metrics
                            <MenubarShortcut><ShortcutKeys shortcuts={["g m"]} /></MenubarShortcut>
                          </MenubarItem>
                        </MenubarGroup>
                      </>
                    )}
                  </MenubarContent>
                </MenubarMenu>

                {showWorkflows ? (
                  <Link
                    href="/workflows"
                    className={menubarLinkClass}
                    data-tour="menu-workflows"
                  >
                    Workflows
                  </Link>
                ) : null}

                <MenubarMenu>
                  <MenubarTrigger>Help</MenubarTrigger>
                  <MenubarContent align="start" className="w-64">
                    <MenubarGroup>
                      <MenubarItem onClick={onStartTour}>
                        <MapIcon data-icon="inline-start" />
                        Product tour
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem onClick={onOpenShortcuts}>
                        <KeyboardIcon data-icon="inline-start" />
                        Keyboard shortcuts
                        <MenubarShortcut><ShortcutKeys shortcuts={["$mod+/"]} /></MenubarShortcut>
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem onClick={onSendFeedback}>
                        <MessageSquareWarningIcon data-icon="inline-start" />
                        Send feedback
                      </MenubarItem>
                      <MenubarItem render={<Link href="/feedback" />}>
                        <ListChecksIcon data-icon="inline-start" />
                        View feedback
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem
                        render={
                          <a href="mailto:support@mercurylabs.io?subject=Twin%20support" />
                        }
                      >
                        <HelpCircleIcon data-icon="inline-start" />
                        Support
                      </MenubarItem>
                    </MenubarGroup>
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </div>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {userControl}
        </div>
      </div>
    </header>
  );
}
